import React, { useMemo, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { SelectHookForm } from 'bench-internal-theme';
import { displayGroupOptions } from './formOptions';
import type { LedgerAccount } from '../types';
import useDisplayGroups from '../useDisplayGroups';

interface DisplayGroupSelectProps {
  ledgerAccount?: LedgerAccount | null;
  isSubmitting: boolean;
}

const DisplayGroupSelect: React.FC<DisplayGroupSelectProps> = ({ ledgerAccount, isSubmitting }) => {
  const { setValue } = useFormContext();
  const { displayGroups, isLoading: loading, hasError: error } = useDisplayGroups();
  const options = displayGroupOptions(displayGroups);

  const defaultValue = useMemo(() => {
    const displayGroup = ledgerAccount?.displayGroup;
    if (!displayGroup?.id) {
      // Return "Hidden" option for null displayGroup
      return {
        value: null,
        label: 'Hidden',
      };
    }

    return {
      value: displayGroup.id,
      label: displayGroup.name,
    };
  }, [ledgerAccount?.displayGroup]);

  useEffect(() => {
    if (defaultValue && options.length > 0 && !loading) {
      setValue('displayGroupId', defaultValue, { shouldDirty: false });
    }
  }, [defaultValue, options.length, loading, setValue]);

  const getPlaceholderText = () => {
    if (error) {
      return 'A problem occurred fetching display groups';
    }
    return 'Select...';
  };

  return (
    <SelectHookForm
      aria-label="Display Group"
      defaultValue={defaultValue}
      isDisabled={isSubmitting || !!error}
      isLoading={loading}
      name="displayGroupId"
      options={options}
      placeholder={getPlaceholderText()}
      rules={{
        validate: (value: { label: string; value: string | null } | null | undefined) => {
          // Ensure a selection has been made (value should be an object with label/value)
          // Allow null value (Hidden option), but not undefined/empty
          if (!value || typeof value !== 'object' || !('label' in value)) {
            return 'Display Group is required';
          }
          return true;
        },
      }}
    />
  );
};

export default DisplayGroupSelect;
